import { TimeTrackerEvents } from '../types';
import { getFunctionSigHash } from '../utils/getFunctionSigHash';
import { linkSmartAccountAndWaitForReceipt } from './linkSmartAccount';
import { encodeDataForLinkingSmartAccount } from '../utils/encodeDataForLinkingSmartAccount';
import { ethAddress, zeroAddress } from 'viem';
import { encodeERC20Approval } from '../utils/encodeERC20Approval';
/**
 * Join a space, potentially linking a wallet if necessary
 */
export async function joinSpace(params) {
    const { spaceDapp, fnArgs, smartAccount, newAccountImplementationType } = params;
    if (!spaceDapp) {
        throw new Error('spaceDapp is required');
    }
    const [spaceId, recipient, signer] = fnArgs;
    const space = spaceDapp.getSpace(spaceId);
    if (!space) {
        throw new Error(`Space with spaceId "${spaceId}" is not found.`);
    }
    const abstractAccountAddress = smartAccount.address;
    if (!abstractAccountAddress) {
        throw new Error('abstractAccountAddress is required');
    }
    const { price: membershipPrice } = await spaceDapp.getJoinSpacePriceDetails(spaceId);
    const callDataJoinSpace = space.Membership.encodeFunctionData('joinSpace(address)', [recipient]);
    const spaceCurrency = await space.Membership.read.getMembershipCurrency();
    const isERC20Space = spaceCurrency.toLowerCase() !== ethAddress.toLowerCase() &&
        spaceCurrency.toLowerCase() !== zeroAddress.toLowerCase();
    //////////////////////////////////////////////////////////////
    // Check if user has already deployed + linked smart account
    //////////////////////////////////////////////////////////////
    if (await spaceDapp.walletLink.checkIfLinked((await signer.getAddress()), abstractAccountAddress)) {
        // they already have a linked wallet, just join the space
        const functionName = 'joinSpace';
        const functionHashForPaymasterProxy = getFunctionSigHash(space.Membership.interface, functionName);
        const price = membershipPrice.toBigInt();
        if (isERC20Space && smartAccount.type === 'simple') {
            throw new Error('Simple accounts are not supported yet for USDC spaces');
        }
        const { toAddress, callData, value } = joinParamsWithUSDCApproval({
            toAddress: [space.Address],
            callData: [callDataJoinSpace],
            value: [price],
            isERC20Space,
            tokenAddress: spaceCurrency,
            spender: space.Address,
            amount: price,
        });
        return smartAccount.sendUserOp({
            toAddress,
            callData,
            value,
            signer,
            spaceId: space.SpaceId,
            functionHashForPaymasterProxy,
        });
    }
    //////////////////////////////////////////////////////////////
    // New users, have not deployed + linked smart account yet
    //////////////////////////////////////////////////////////////
    // new users w/ simple accounts
    if (newAccountImplementationType === 'simple') {
        // if the user does not have a linked wallet, we need to link their smart account first b/c that is where the memberhship NFT will be minted
        // joinSpace might require a value, if the space has a fixed membership cost
        //
        // But SimpleAccount does not support executeBatch with values
        // A new user who is joining a paid space will encounter this scenario
        //
        // Therefore, we need to link the wallet first, then join the space
        // Another smart account contract should support this and allow for a single user operation
        await linkSmartAccountAndWaitForReceipt({
            abstractAccountAddress,
            sequenceName: TimeTrackerEvents.JOIN_SPACE,
            smartAccount,
            spaceDapp,
            timeTracker: undefined,
            signer,
        });
        if (isERC20Space) {
            throw new Error('USDC spaces are not supported yet for simple accounts');
        }
        return smartAccount.sendUserOp({
            toAddress: space.Address,
            value: membershipPrice.toBigInt(),
            callData: callDataJoinSpace,
            signer,
            spaceId: space.SpaceId,
            functionHashForPaymasterProxy: 'joinSpace',
        });
    }
    // new users w/ modular accounts
    else if (newAccountImplementationType === 'modular') {
        const callDataForLinkingSmartAccount = await encodeDataForLinkingSmartAccount(spaceDapp, signer, abstractAccountAddress);
        const { toAddress, callData, value } = joinParamsWithUSDCApproval({
            toAddress: [spaceDapp.walletLink.address, space.Address],
            callData: [
                callDataForLinkingSmartAccount,
                callDataJoinSpace,
            ],
            value: [0n, membershipPrice.toBigInt()],
            isERC20Space,
            tokenAddress: spaceCurrency,
            spender: space.Address,
            amount: membershipPrice.toBigInt(),
        });
        return smartAccount.sendUserOp({
            toAddress,
            value,
            callData,
            signer,
            spaceId: space.SpaceId,
            functionHashForPaymasterProxy: 'joinSpace',
        });
    }
    else {
        throw new Error('Invalid account implementation type');
    }
}
function joinParamsWithUSDCApproval(params) {
    const { toAddress, callData, isERC20Space, tokenAddress, spender, amount, value } = params;
    if (isERC20Space) {
        const usdcApprovalCallData = encodeERC20Approval({
            tokenAddress,
            spender,
            amount,
        });
        return {
            toAddress: [tokenAddress, ...toAddress],
            callData: [usdcApprovalCallData, ...callData],
            value: undefined,
        };
    }
    return {
        toAddress,
        callData,
        value,
    };
}
